import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { pipe, Array as A } from 'effect';
import type { FilterModel } from 'ag-grid-community';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

import Tooltip from '@/components/mui/Tooltip';
import { messages as agGridMessages } from '@/components/AgGrid/helpers/translations';
import { currentCompanyDateFormatSelector } from '@/domain/companies';

import { Chip } from '@/pages/company/BankFeedRoute/components/ActiveFilterChips/StyledComponents';
import {
  formatFilterValue,
  type OperatorLabels,
} from '@/pages/company/BankFeedRoute/components/ActiveFilterChips/formatFilterValue';

export type ActiveFilterChipsProps = {
  filterModel: FilterModel;
  getColumnHeader: (colId: string) => string;
  onChipDelete: (colId: string) => void;
  onReset: () => void;
};

const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  filterModel,
  getColumnHeader,
  onChipDelete,
  onReset,
}) => {
  const { formatMessage } = useIntl();
  // Same company date format the date cells use — keeps chip dates and grid cells in sync.
  const companyDateFormat = useSelector(currentCompanyDateFormatSelector);

  // Operator labels reuse the same dictionary AG Grid's own header-menu popup
  // displays (via `getLocaleText` wired in BankFeedGrid) — chip text matches the
  // option the user just clicked.
  const operatorLabels = useMemo<OperatorLabels>(
    () => ({
      operators: {
        contains: formatMessage(agGridMessages.contains),
        notContains: formatMessage(agGridMessages.notContains),
        equals: formatMessage(agGridMessages.equals),
        notEqual: formatMessage(agGridMessages.notEqual),
        startsWith: formatMessage(agGridMessages.startsWith),
        endsWith: formatMessage(agGridMessages.endsWith),
        blank: formatMessage(agGridMessages.blank),
        notBlank: formatMessage(agGridMessages.notBlank),
        greaterThan: formatMessage(agGridMessages.greaterThan),
        greaterThanOrEqual: formatMessage(agGridMessages.greaterThanOrEqual),
        lessThan: formatMessage(agGridMessages.lessThan),
        lessThanOrEqual: formatMessage(agGridMessages.lessThanOrEqual),
        // Date operators (custom date filter). `beforeOrOn`/`afterOrOn` need a label;
        // `inRange`, `withInMonth`, `withInYear` are special-cased in `formatDate`
        // (range / 'MMM YYYY' / 'YYYY') and render no operator label, mirroring Workspace.
        afterOrOn: formatMessage(agGridMessages.afterOrOn),
        beforeOrOn: formatMessage(agGridMessages.beforeOrOn),
      },
      and: formatMessage(agGridMessages.andCondition),
      or: formatMessage(agGridMessages.orCondition),
      dateFormat: companyDateFormat || 'DD/MM/YYYY',
    }),
    [formatMessage, companyDateFormat],
  );

  const chips = useMemo(
    () =>
      pipe(
        Object.entries(filterModel),
        A.map(([colId, entry]) => ({
          colId,
          header: getColumnHeader(colId),
          value: formatFilterValue(entry, operatorLabels),
        })),
        A.filter(({ value }) => value.length > 0),
      ),
    [filterModel, getColumnHeader, operatorLabels],
  );

  if (chips.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
      {chips.map(({ colId, header, value }) => (
        <Tooltip key={colId} t={`${header}: ${value}`} placement="top">
          <Chip
            size="small"
            variant="outlined"
            onDelete={() => onChipDelete(colId)}
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                  {`${header}:`}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  noWrap
                  sx={{ color: 'text.primary', fontWeight: 500 }}
                >
                  {value}
                </Typography>
              </Stack>
            }
          />
        </Tooltip>
      ))}
      <Link
        component="button"
        type="button"
        variant="body2"
        underline="hover"
        onClick={onReset}
        sx={{ whiteSpace: 'nowrap' }}
      >
        <FormattedMessage
          id="company.bankFeed.transactions.columnFilters.reset"
          defaultMessage="Reset filters"
        />
      </Link>
    </Box>
  );
};

export default ActiveFilterChips;
